import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Users, Building2, TrendingUp, Search as SearchIcon, Home as HomeIcon, KeyRound, Star, UserPlus } from 'lucide-react';
import SearchBar from '../components/SearchBar';
import PropertyCard from '../components/PropertyCard';
import { Button } from '../components/ui/button';
import api, { enrichProperty, enrichDeveloper, enrichTestimonial, enrichAgent } from '../lib/api';
import { useSite } from '../context/SiteContext';
import { getText } from '../lib/siteContent';

export default function Home() {
  const { settings } = useSite();
  const [featured, setFeatured] = useState([]);
  const [newest, setNewest] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [agents, setAgents] = useState([]);

  useEffect(() => {
    const asArray = (data) => Array.isArray(data) ? data : (data?.items || []);
    api.get('/properties/featured').then(r => setFeatured(asArray(r.data).map(enrichProperty)));
    api.get('/properties', { params: { sort: 'newest', limit: 6, skip: 6 } }).then(r => setNewest((r.data?.items || []).map(enrichProperty)));
    api.get('/developers').then(r => setDevelopers(asArray(r.data).map(enrichDeveloper)));
    api.get('/testimonials').then(r => setTestimonials(asArray(r.data).map(enrichTestimonial)));
    api.get('/agents').then(r => setAgents(asArray(r.data).map(enrichAgent)));
  }, []);

  const company = {
    name: settings?.companyName || 'Terra Homes Realty',
    tagline: settings?.tagline || 'Your Home, Our Goal',
    heroTitle: settings?.heroTitle || 'Find Your Place in the Philippines',
    heroSubtitle: settings?.heroSubtitle || 'Browse thousands of verified houses, condos, lots, and commercial properties.',
    heroImage: settings?.heroImage || 'https://images.unsplash.com/photo-1535498730771-e735b998cd64?auto=format&fit=crop&w=1920&q=80'
  };
  const t = (key) => getText(settings, key);
  const stats = [
    { icon: Building2, value: t('home_stat1_value'), label: t('home_stat1_label') },
    { icon: Users, value: t('home_stat2_value'), label: t('home_stat2_label') },
    { icon: ShieldCheck, value: t('home_stat3_value'), label: t('home_stat3_label') },
    { icon: Star, value: t('home_stat4_value'), label: t('home_stat4_label') },
  ];

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[640px] flex items-center">
        <img src={company.heroImage} alt="Featured property" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto w-full px-6 py-20">
          <div className="max-w-3xl text-white fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-[#DC2626] text-xs font-semibold tracking-wider uppercase rounded">
              <Star className="w-3 h-3 fill-white" /> {company.tagline}
            </span>
            <h1 className="mt-4 text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight">{company.heroTitle}</h1>
            <p className="mt-4 text-lg md:text-xl text-neutral-200 max-w-2xl">{company.heroSubtitle}</p>
            <Link to="/contact?subject=agent">
              <Button className="mt-6 h-12 px-6 gap-2 bg-white text-neutral-900 hover:bg-neutral-100 font-semibold shadow-lg">
                <UserPlus className="w-5 h-5 text-[#DC2626]" /> {t('home_agent_cta')}
              </Button>
            </Link>
          </div>
          <div className="mt-8 max-w-5xl"><SearchBar /></div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-5xl">
            {stats.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/10 backdrop-blur-sm border border-white/10 text-white">
                  <div className="w-9 h-9 rounded-md bg-[#DC2626]/90 flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4" /></div>
                  <div><p className="text-sm font-bold leading-tight">{s.value}</p><p className="text-xs text-neutral-300">{s.label}</p></div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-[#DC2626]">{t('home_featured_eyebrow')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-1">{t('home_featured_title')}</h2>
            <p className="text-neutral-600 mt-2 max-w-2xl">{t('home_featured_subtitle')}</p>
          </div>
          <Link to="/properties"><Button variant="outline" className="gap-2 border-neutral-300">View All <ArrowRight className="w-4 h-4" /></Button></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((p) => <PropertyCard key={p.id} property={p} />)}
        </div>
      </section>

      {/* NEWEST */}
      {newest.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-8 pb-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-[#DC2626]">{t('home_newest_eyebrow')}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-1">{t('home_newest_title')}</h2>
            </div>
            <Link to="/properties"><Button variant="outline" className="gap-2 border-neutral-300">Browse All <ArrowRight className="w-4 h-4" /></Button></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {newest.map((p) => <PropertyCard key={p.id} property={p} />)}
          </div>
        </section>
      )}

      {/* DEVELOPERS */}
      {developers.length > 0 && (
        <section className="bg-neutral-950 text-white py-16 overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 mb-8">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#DC2626]">{company.name} {'\u00B7'} Trusted Partners</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-1">{t('home_devs_title')}</h2>
            <p className="text-neutral-400 mt-2 max-w-2xl">{t('home_devs_subtitle')}</p>
          </div>
          <div className="relative">
            <div className="flex marquee-track gap-6 w-max">
              {[...developers, ...developers].map((d, idx) => (
                <div key={idx} className="flex-shrink-0 w-64 bg-neutral-900 border border-neutral-800 rounded-lg p-5 flex flex-col items-center">
                  <img src={d.logo} alt={d.name} className="h-20 w-auto object-contain mb-3" />
                  <p className="font-semibold">{d.name}</p>
                  <p className="text-xs text-neutral-400 mt-1">{d.tagline}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#DC2626]">{t('home_how_eyebrow')}</span>
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-1">{t('home_how_title')}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: SearchIcon, title: t('home_step1_title'), desc: t('home_step1_desc') },
            { icon: HomeIcon, title: t('home_step2_title'), desc: t('home_step2_desc') },
            { icon: KeyRound, title: t('home_step3_title'), desc: t('home_step3_desc') }
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="p-8 rounded-xl border border-neutral-200 bg-white text-center card-hover">
                <div className="inline-flex w-14 h-14 rounded-full bg-red-50 text-[#DC2626] items-center justify-center mb-4">
                  <Icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-neutral-900">{s.title}</h3>
                <p className="text-neutral-600 mt-2">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* AGENTS PREVIEW */}
      {agents.length > 0 && (
        <section className="bg-neutral-50 py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-[#DC2626]">{t('home_agents_eyebrow')}</span>
                <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-1">{t('home_agents_title')}</h2>
              </div>
              <Link to="/agents"><Button variant="outline" className="gap-2 border-neutral-300">All Advisors <ArrowRight className="w-4 h-4" /></Button></Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
              {agents.slice(0, 6).map((a) => (
                <Link to={'/agent/' + a.id} key={a.id} className="text-center group">
                  <div className="aspect-square overflow-hidden rounded-full border-4 border-white shadow-md mb-3">
                    <img src={a.photo} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  </div>
                  <p className="font-semibold text-neutral-900 text-sm">{a.name}</p>
                  <p className="text-xs text-neutral-500">{a.title}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-16">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#DC2626]">{t('home_testimonials_eyebrow')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-1">{t('home_testimonials_title')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.id} className="p-7 rounded-xl border border-neutral-200 bg-white card-hover">
                <div className="flex gap-1 text-[#DC2626] mb-3">{Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}</div>
                <p className="text-neutral-700 italic">{t.quote}</p>
                <div className="mt-5 flex items-center gap-3">
                  <img src={t.avatar} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
                  <div>
                    <p className="font-semibold text-neutral-900">{t.name}</p>
                    <p className="text-xs text-neutral-500">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative py-20 overflow-hidden">
        <img src={company.heroImage} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-neutral-950/80" />
        <div className="relative max-w-4xl mx-auto px-6 text-center text-white">
          <TrendingUp className="w-10 h-10 text-[#DC2626] mx-auto mb-3" />
          <h2 className="text-3xl md:text-5xl font-bold">{t('home_cta_title')}</h2>
          <p className="mt-3 text-lg text-neutral-300 max-w-2xl mx-auto">{t('home_cta_subtitle')}</p>
          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            <Link to="/properties"><Button className="btn-primary h-12 px-6">{t('home_cta_primary')}</Button></Link>
            <Link to="/contact"><Button variant="outline" className="h-12 px-6 bg-transparent border-white text-white hover:bg-white hover:text-neutral-900">{t('home_cta_secondary')}</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
